import { Badge, BadgeVariants } from "~/components/Badge";
import { uniqueValues } from "~/utils/functions/uniqueValues";
import { Tooltip } from "~/components/Tooltip";
import { Emdash } from "~/components/Punctuation/Emdash";
import { Anchor } from "~/components/Anchor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkSquare } from "@fortawesome/pro-regular-svg-icons";
import { extractDomainName } from "~/utils/extractDomainName";
import { cn } from "~/lib/utils";
import { AssetUserEvents } from "~/modules/analytics/events/assets";

// // // //

function AnchorPassThru(props: {
    href: string | null;
    children: React.ReactNode;
}) {
    const { href, children } = props;

    if (href === null) {
        return <>{children}</>;
    }

    return (
        <Anchor
            href={href}
            openInNewTab
            analyticsLabel={AssetUserEvents.TransparencyCellLink}
            variant="default"
        >
            {children}
        </Anchor>
    );
}

// // // //

export function TransparencyCell(props: {
    type: string | null;
    description: string | null;
    url: string | null;
    justify?: "left" | "right";
}) {
    const { type, description, url, justify } = props;

    const values = type?.split(",") || [];

    // if (type === null) {
    //     return <Emdash />;
    // }

    const allValues = values.filter((each) => each.trim() !== "");
    const items = uniqueValues(allValues);

    if (items.length === 0) {
        return <Emdash />;
    }

    const wrapperClassName = cn("flex flex-col gap-1", {
        "items-end": justify === "right",
    });

    if (items.length < 3) {
        if (description === null && url === null) {
            return (
                <div className={wrapperClassName}>
                    {items.map((ac, index) => {
                        return (
                            <Badge
                                key={`${ac}-${index}`}
                                size="sm"
                                rounded="sm"
                                className="w-min whitespace-nowrap"
                                label={ac}
                                variant={BadgeVariants.gray}
                            />
                        );
                    })}
                </div>
            );
        }

        // Get domain name for tooltip content with error handling
        let domainName = "";
        if (url !== null) {
            try {
                domainName = extractDomainName(url) || url;
            } catch {
                domainName = url;
            }
        }

        return (
            <Tooltip
                placement="top"
                tooltipContent={
                    <div className="w-max px-3 py-1.5 text-xs">
                        {url !== null && (
                            <p className="text-blue-300 flex items-center gap-1">
                                <FontAwesomeIcon
                                    icon={faExternalLinkSquare}
                                    className="h-3"
                                />
                                Click to read more on {domainName}
                            </p>
                        )}

                        {description !== null && (
                            <>
                                <p
                                    className={cn("mb-1 font-semibold", {
                                        "mt-1": url !== null,
                                    })}
                                >
                                    Description
                                </p>
                                <p className="max-w-120">{description}</p>
                            </>
                        )}
                    </div>
                }
            >
                {({ setTriggerRef }) => (
                    <AnchorPassThru href={url}>
                        <div ref={setTriggerRef} className={wrapperClassName}>
                            {items.map((ac, index) => {
                                return (
                                    <Badge
                                        key={`${ac}-${index}`}
                                        size="sm"
                                        rounded="sm"
                                        hover
                                        className="w-min whitespace-nowrap"
                                        label={
                                            <>
                                                {ac}
                                                <FontAwesomeIcon
                                                    icon={faExternalLinkSquare}
                                                    className="h-3 ml-1.5"
                                                />
                                            </>
                                        }
                                        variant={BadgeVariants.gray}
                                    />
                                );
                            })}
                        </div>
                    </AnchorPassThru>
                )}
            </Tooltip>
        );
    }

    return (
        <Tooltip
            placement="top"
            tooltipContent={
                <div className="w-max px-3 py-1.5 text-xs">
                    {url !== null && (
                        <p className="text-blue-300 flex items-center gap-1 mb-1">
                            <FontAwesomeIcon
                                icon={faExternalLinkSquare}
                                className="h-3"
                            />
                            Click to read more
                        </p>
                    )}
                    {description !== null && (
                        <>
                            <p className="mb-1 font-semibold">Description</p>
                            <p className="max-w-120 mb-2">{description}</p>
                        </>
                    )}
                    <ul>
                        {items.map((ac, index) => (
                            <li
                                key={`${ac}-${index}`}
                                className="whitespace-nowrap text-xs"
                            >
                                {ac}
                            </li>
                        ))}
                    </ul>
                </div>
            }
        >
            {({ setTriggerRef }) => (
                <AnchorPassThru href={url}>
                    <div ref={setTriggerRef} className={wrapperClassName}>
                        <Badge
                            size="sm"
                            rounded="sm"
                            className="w-min whitespace-nowrap"
                            label={
                                <>
                                    {items[0]}
                                    <FontAwesomeIcon
                                        icon={faExternalLinkSquare}
                                        className="h-3 ml-1.5"
                                    />
                                </>
                            }
                            hover
                            variant={BadgeVariants.gray}
                        />
                        <Badge
                            size="sm"
                            rounded="sm"
                            hover
                            className="w-min whitespace-nowrap"
                            label={<>{`+ ${items.length - 1} More`}</>}
                            variant={BadgeVariants.blue}
                        />
                    </div>
                </AnchorPassThru>
            )}
        </Tooltip>
    );
}
